import React, { useContext, useEffect, useRef, useCallback } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  platformState,
  activeScopeState,
  wsReadyState,
  cloudReadyState,
  userInfoState,
  scopesState,
  scopePreferenceState,
} from "../global-state";
import { v4 as uuidv4 } from "uuid";
import { useEventEmitter } from "./event-context";
import { SAND_CAT_WS } from "../config";
import { useAuth } from "../auth/auth-provider";
import WebSocketConnectionToast from "./websocket-connection-toast";
import { useAppInterface } from "../app/app-interface-provider";
import { useHistory } from "react-router-dom";
import { switchScope } from "../service/scope-service";

const WebSocketContext = React.createContext();

// Global ref for activeScope - update this BEFORE calling setActiveScope anywhere in the project
export const activeScopeRef = { current: null };

const excludedPaths = [
  "/user-login",
  "/user-login-callback",
  "/user-logout",
  "/developer-logout",
  "/developer-login",
  "/developer-login-callback",
  "/desktop-auth-wait",
  "/desktop-auth-done",
];

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [wsReady, setWsReady] = useRecoilState(wsReadyState);
  const [cloudReady, setCloudReady] = useRecoilState(cloudReadyState);
  const platform = useRecoilValue(platformState);
  const [activeScope, setActiveScope] = useRecoilState(activeScopeState);
  const setUserInfo = useSetRecoilState(userInfoState);
  const setScopes = useSetRecoilState(scopesState);
  const setScopePreference = useSetRecoilState(scopePreferenceState);
  const { emit } = useEventEmitter();
  const appInterface = useAppInterface();
  const messageQueue = useRef({});
  const wsRef = useRef(null);
  const retryDuration = useRef(500);
  const { checkAuthState } = useAuth();
  const history = useHistory();

  const heartbeatInterval = useRef(null);
  const invisibleStartTime = useRef(null);
  const lastMessageTime = useRef(Date.now());
  const reconnectTimeout = useRef(null);
  const isPageVisible = useRef(true);
  const isPageIdle = useRef(false);
  const ipcListenersRegistered = useRef(false);

  // Keep global activeScopeRef in sync with activeScope state as fallback
  // Note: You should update activeScopeRef.current BEFORE calling setActiveScope for immediate updates
  useEffect(() => {
    activeScopeRef.current = activeScope;
  }, []);

  // desktop platform only
  useEffect(() => {
    if (platform.source === "desktop") {
      if (!ipcListenersRegistered.current) {
        const handleWsReady = (isReady) => {
          console.log("ws:ready", isReady);
          setWsReady(isReady);
        };

        const handleWsMessage = (data) => {
          console.log("ws:message", data);
          if (data.type === "sd" || data.type === "srq") {
            data.realtime = true;
            emit(data.target, data);
          }
        };

        appInterface.addEventListener("ws:ready", handleWsReady);
        appInterface.addEventListener("ws:message", handleWsMessage);
        appInterface.getWebsocketStatus().then((status) => {
          setWsReady(status.ready);
        });

        ipcListenersRegistered.current = true;

        return () => {
          appInterface.removeEventListener("ws:ready", handleWsReady);
          appInterface.removeEventListener("ws:message", handleWsMessage);
          ipcListenersRegistered.current = false;
        };
      }
    }
  }, []);

  // app/desktop platform - handle window.onAppInit callback
  useEffect(() => {
    if (platform.source === "app" || platform.source === "desktop") {
      window.onAppInit = (
        ready,
        cloudReadyParam,
        userInfoString,
        scopeInfoString
      ) => {
        setWsReady(ready);
        setCloudReady(cloudReadyParam);
        const userInfoData =
          typeof userInfoString === "string"
            ? JSON.parse(userInfoString)
            : userInfoString;
        const scopeInfo =
          typeof scopeInfoString === "string"
            ? JSON.parse(scopeInfoString)
            : scopeInfoString;
        setUserInfo(userInfoData);

        if (scopeInfo.activeScope) {
          switchScope(sendAndWaitWs, scopeInfo.activeScope);
        }
        changeActiveScope(scopeInfo.activeScope);
        setScopes(scopeInfo.scopes);
        setScopePreference(scopeInfo.scopePreferences);
      };
    }
  }, [platform.source]);

  // initial connection
  useEffect(() => {
    if (platform.source == "web") {
      console.log("Refreshing connection as activeScope changed");
      cleanupConnection();
      resetReconnect();
    }
  }, []);

  // handle visibility change
  useEffect(() => {
    if (platform.source === "web") {
      const handleVisibilityChange = () => {
        console.log("visibilitychange", document.visibilityState);
        isPageVisible.current = document.visibilityState === "visible";
        if (isPageVisible.current) {
          console.log("Page became visible, try reconnecting...");
          isPageIdle.current = false;
          invisibleStartTime.current = null;
          resetReconnect();
        } else {
          console.log("Page became hidden");
          if (!invisibleStartTime.current) {
            invisibleStartTime.current = Date.now();
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
  }, []);

  useEffect(() => {
    if (platform.source === "app") {
      const status = appInterface.getWsStatus();
      console.log("Fetch WS status on init:", status);
      setWsReady(status || false);
    }
  }, []);

  const resetReconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
      retryDuration.current = 500;
    }
    reconnectTimeout.current = setTimeout(() => reconnect(), 0);
  };

  const delayedReconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    reconnectTimeout.current = setTimeout(
      () => reconnect(),
      retryDuration.current
    );
  };

  const reconnect = async () => {
    if (platform.source !== "web") {
      return;
    }
    if (retryDuration.current < 300000) {
      retryDuration.current = retryDuration.current * 2;
    }
    if (!isPageVisible.current) {
      return;
    }
    await connectWebSocket();
  };

  const startHeartbeat = (heartbeatSec) => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "pi" }));
        } catch (error) {
          console.warn("Failed to send ping:", error);
          cleanupConnection();
        }
        if (!isPageVisible.current && platform.source === "web") {
          if (!invisibleStartTime.current) {
            invisibleStartTime.current = Date.now();
          }
          const invisibleDuration = Date.now() - invisibleStartTime.current;
          if (invisibleDuration > 300000) {
            cleanupConnection();
          }
        } else {
          invisibleStartTime.current = null;
        }
      }
    }, heartbeatSec * 1000);
  };

  const cleanupConnection = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsReady(false);
  };

  const connectWebSocket = async () => {
    if (platform.source !== "web") {
      return;
    }
    if (excludedPaths.includes(location.pathname)) {
      return;
    }

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    console.debug("WebSocket connecting...");
    let socketUrl = SAND_CAT_WS;
    const accessToken = await checkAuthState();
    console.debug("WebSocket connecting...", socketUrl);
    const websocket = new WebSocket(socketUrl);
    websocket.onopen = async () => {
      // TODO: use dev token
      const resp = await sendAndWaitWs(
        "/auth",
        JSON.stringify({
          token: `Bearer ${accessToken}`,
          source: platform.source,
          activeScope: activeScope,
        })
      );
      if (resp.error) {
        console.error("WebSocket auth failed:", resp.error);
        websocket.close();
        return;
      }
      console.debug("WebSocket auth success:", resp);
      const config = JSON.parse(resp.payload);
      setWsReady(true);
      wsRef.current = websocket;
      retryDuration.current = 500;
      lastMessageTime.current = Date.now();
      startHeartbeat(config.heartbeatInterval);
      setUserInfo(config.userInfo);
      setScopes(config.scopes);
      setScopePreference(config.scopePreferences);
      if (config.activeScope) {
        changeActiveScope(config.activeScope);
        if (appInterface.isAvailable()) {
          await appInterface.saveScopes({
            scopes: config.scopes,
            scopePreferences: config.scopePreferences,
            activeScope: config.activeScope,
          });
        }
      } else {
        history.replace("/init-scope");
        return;
      }
    };

    websocket.onmessage = (event) => {
      lastMessageTime.current = Date.now();

      const data = JSON.parse(event.data);
      if (data.type === "po") {
        return;
      }
      if (data.type === "srp" && data.sig) {
        if (messageQueue.current[data.sig]) {
          messageQueue.current[data.sig].resolve(data);
          delete messageQueue.current[data.sig];
        }
      }
      if (data.type === "sd" || data.type === "srq") {
        data.realtime = true;
        emit(data.target, data);
      }
    };

    websocket.onclose = async (event) => {
      console.log("WebSocket connection closed:", event);
      if (wsRef.current === websocket) {
        setWsReady(false);
      }
      emit("_wsclosed_", null);

      delayedReconnect();
    };

    websocket.onerror = (error) => {
      console.error("WebSocket Error:", error);
      websocket.close();
    };

    wsRef.current = websocket;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupConnection();
    };
  }, []);

  // Function to change active scope - updates ref before state
  const changeActiveScope = useCallback(
    (scopeId) => {
      activeScopeRef.current = scopeId;
      setActiveScope(scopeId);
    },
    [setActiveScope]
  );

  // Function to send a message and wait for a response
  const sendAndWaitWs = useCallback((target, payload, scopeId = null) => {
    if (platform.source !== "web") {
      return appInterface.sendWebsocketAndWait(
        scopeId || activeScopeRef.current,
        target,
        payload
      );
    }

    const message = {
      scopeId: scopeId || activeScopeRef.current,
      target,
      sig: uuidv4(),
      payload,
      type: "arq",
    };
    return new Promise((resolve, reject) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        messageQueue.current[message.sig] = { resolve, reject };
        wsRef.current.send(JSON.stringify(message));
      } else {
        reject(new Error("Websocket connection is not ready"));
      }
    });
  }, []);

  // Function to send a message without waiting for a response
  const sendAndForgetWs = useCallback((target, payload, scopeId = null) => {
    if (platform.source !== "web") {
      return appInterface.sendWebsocketAndForget(
        scopeId || activeScopeRef.current,
        target,
        payload
      );
    }

    const message = {
      scopeId: scopeId || activeScopeRef.current,
      target,
      sig: uuidv4(),
      payload,
      type: "ad",
    };
    return new Promise((resolve, reject) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
        resolve({});
      } else {
        reject(new Error("Websocket connection is not ready"));
      }
    });
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        sendAndWaitWs,
        sendAndForgetWs,
        resetReconnect,
        changeActiveScope,
      }}
    >
      {children}
      <WebSocketConnectionToast />
    </WebSocketContext.Provider>
  );
};
